import type { Transition, Variants } from 'motion/react';

/**
 * Shared motion presets so every animated component moves the same way.
 * Durations are short on purpose: UI feedback should feel quick, never in the way.
 */

/** Ease-out-expo: fast start, soft landing. */
export const easeOut = [0.16, 1, 0.3, 1] as const;
/** Ease-in for things leaving the screen. */
export const easeIn = [0.7, 0, 0.84, 0] as const;

export const pressTransition: Transition = { type: 'spring', stiffness: 600, damping: 30 };

/** Scale a pressable element down slightly while it's held. */
export const pressScale = 0.96;

const enter = (duration: number): Transition => ({ duration, ease: easeOut });
const exit = (duration: number): Transition => ({ duration, ease: easeIn });

export const backdropVariants: Variants = {
  open: { opacity: 1, transition: enter(0.2) },
  closed: { opacity: 0, transition: exit(0.15) },
};

export const dialogVariants: Variants = {
  open: { opacity: 1, scale: 1, y: 0, transition: enter(0.28) },
  closed: { opacity: 0, scale: 0.96, y: 12, transition: exit(0.16) },
};

export type DrawerSide = 'left' | 'right';

export const drawerVariants: Variants = {
  open: { x: 0, transition: enter(0.32) },
  closed: (side: DrawerSide = 'right') => ({
    x: side === 'left' ? '-100%' : '100%',
    transition: exit(0.2),
  }),
};

export const menuVariants: Variants = {
  open: { opacity: 1, scale: 1, y: 0, transition: enter(0.18) },
  closed: { opacity: 0, scale: 0.95, y: -4, transition: exit(0.12) },
};

export const collapseVariants: Variants = {
  open: {
    height: 'auto',
    opacity: 1,
    transition: { height: enter(0.3), opacity: enter(0.2) },
    // let focus rings and shadows show once it's fully open
    transitionEnd: { overflow: 'visible' },
  },
  closed: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
    transition: { height: exit(0.2), opacity: exit(0.12) },
  },
};

export const fadeScaleVariants: Variants = {
  open: { opacity: 1, scale: 1, transition: enter(0.2) },
  closed: { opacity: 0, scale: 0.8, transition: exit(0.15) },
};

/** Icon swap (e.g. sun ↔ moon): rotate in, rotate out. */
export const iconSwapVariants: Variants = {
  initial: { opacity: 0, rotate: -90, scale: 0.6 },
  animate: { opacity: 1, rotate: 0, scale: 1, transition: enter(0.3) },
  exit: { opacity: 0, rotate: 90, scale: 0.6, transition: exit(0.15) },
};
